import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faPenToSquare, faFloppyDisk, faChartColumn, faFilePdf } from '@fortawesome/free-solid-svg-icons';

const API = process.env.REACT_APP_API_URL || '';
const getAccountKey = () => localStorage.getItem('kotak_ucc') || localStorage.getItem('kotak_mobile') || 'default';
const money = (v) => `₹ ${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const monthLabel = (date) => date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export default function PNL() {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(iso(new Date()));
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const from = iso(new Date(month.getFullYear(), month.getMonth(), 1));
      const to = iso(new Date(month.getFullYear(), month.getMonth() + 1, 0));
      const response = await axios.get(`${API}/api/pnl`, { params: { accountKey: getAccountKey(), from, to } });
      setEntries(response.data?.entries || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load PNL.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [month]);

  const byDate = useMemo(() => Object.fromEntries(entries.map(e => [e.date, { ...e, pnlAmount: Number(e.pnlAmount) } ])), [entries]);
  const stats = useMemo(() => {
    const values = entries.map(e => Number(e.pnlAmount)).filter(Number.isFinite);
    const profits = values.filter(v => v > 0);
    const losses = values.filter(v => v < 0);
    return {
      net: values.reduce((a, b) => a + b, 0),
      profitDays: profits.length,
      lossDays: losses.length,
      avgProfit: profits.length ? profits.reduce((a, b) => a + b, 0) / profits.length : 0,
      avgLoss: losses.length ? losses.reduce((a, b) => a + b, 0) / losses.length : 0,
    };
  }, [entries]);

  const calendarDays = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const start = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < start; i += 1) cells.push(null);
    for (let d = 1; d <= total; d += 1) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [month]);

  const chart = useMemo(() => {
    const days = entries.map(e => ({ date: e.date, value: Number(e.pnlAmount) })).filter(e => Number.isFinite(e.value));
    const max = Math.max(...days.map(d => Math.abs(d.value)), 1);
    return { days, max };
  }, [entries]);

  const selectDate = (date) => {
    if (!date) return;
    const key = iso(date);
    const entry = byDate[key];
    setSelectedDate(key);
    setAmount(entry ? String(entry.pnlAmount) : '');
    setNotes(entry?.notes || '');
    setEditing(false);
  };

  useEffect(() => {
    const current = new Date();
    if (current.getFullYear() === month.getFullYear() && current.getMonth() === month.getMonth()) selectDate(current);
    else selectDate(new Date(month.getFullYear(), month.getMonth(), 1));
  }, [entries, month]);

  const exportMonthlyPdf = () => {
    const label = monthLabel(month);
    const rows = entries.map((entry) => {
      const value = Number(entry.pnlAmount || 0);
      return `<tr><td>${entry.date}</td><td class="${value >= 0 ? 'profit' : 'loss'}">${value >= 0 ? '+' : ''}${money(value)}</td><td>${String(entry.notes || '').replace(/[&<>]/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c]))}</td></tr>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Monthly PNL - ${label}</title><style>
      @page{size:A4 portrait;margin:14mm}*{box-sizing:border-box}body{font-family:Poppins,Arial,sans-serif;color:#293740;margin:0;font-size:10px;-webkit-print-color-adjust:exact;print-color-adjust:exact}h1{font-size:20px;margin:0 0 4px}h2{font-size:12px;margin:0 0 18px;color:#138a63}p{margin:3px 0;color:#6f7d84}.brand{font-size:10px;font-weight:700;letter-spacing:1px;color:#138a63;margin-bottom:14px}.stats{display:grid;grid-template-columns:repeat(5,1fr);gap:7px;margin:14px 0 18px}.stat{border:1px solid #dfe6e2;border-radius:7px;padding:9px;background:#fafcfb}.stat span{display:block;font-size:7px;color:#7b888f;font-weight:700;letter-spacing:.5px}.stat b{display:block;margin-top:5px;font-size:11px}.profit{color:#138a63}.loss{color:#c85151}table{width:100%;border-collapse:collapse;margin-top:10px}th{background:#f2f6f4;color:#53616a;font-size:8px;text-align:left;text-transform:uppercase;letter-spacing:.5px}th,td{border:1px solid #dfe6e2;padding:7px}td:nth-child(2){font-weight:700;text-align:right}tfoot td{font-weight:700;background:#f7faf8}.footer{margin-top:18px;border-top:1px solid #dfe6e2;padding-top:8px;font-size:7px;color:#87939a}@media print{body{font-family:Poppins,Arial,sans-serif}}
    </style></head><body><div class="brand">MK TRADER · PNL REPORT</div><h1>Monthly PNL Report</h1><h2>${label}</h2><p>Generated from the Daily PNL module</p><div class="stats"><div class="stat"><span>MONTHLY NET PNL</span><b class="${stats.net >= 0 ? 'profit' : 'loss'}">${money(stats.net)}</b></div><div class="stat"><span>PROFIT DAYS</span><b>${stats.profitDays}</b></div><div class="stat"><span>LOSS DAYS</span><b>${stats.lossDays}</b></div><div class="stat"><span>AVG PROFIT</span><b class="profit">${money(stats.avgProfit)}</b></div><div class="stat"><span>AVG LOSS</span><b class="loss">${money(stats.avgLoss)}</b></div></div><table><thead><tr><th>Date</th><th>PNL Amount</th><th>Notes</th></tr></thead><tbody>${rows || '<tr><td colspan="3">No PNL entries for this month.</td></tr>'}</tbody><tfoot><tr><td>Total</td><td class="${stats.net >= 0 ? 'profit' : 'loss'}">${money(stats.net)}</td><td>${entries.length} recorded day(s)</td></tr></tfoot></table><div class="footer">A4 report · Font: Poppins · ${new Date().toLocaleString('en-IN')}</div><script>window.onload=()=>{setTimeout(()=>window.print(),150)}</script></body></html>`;
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) { setError('Please allow pop-ups to export the PDF.'); return; }
    win.document.open(); win.document.write(html); win.document.close();
  };

  const save = async () => {
    if (!selectedDate || !Number.isFinite(Number(amount))) return;
    setSaving(true); setError('');
    try {
      await axios.put(`${API}/api/pnl`, { accountKey: getAccountKey(), date: selectedDate, pnlAmount: Number(amount), notes });
      setEditing(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save PNL.');
    } finally { setSaving(false); }
  };

  return (
    <main className="terminal-page pnl-page">
      <div className="pnl-page-head">
        <div><small>PNL</small><h2>Daily PNL</h2><span>Server snapshots at 3:35 PM and 11:30 PM · manual update available</span></div>
        <div className="pnl-head-actions"><button className="btn btn-sm btn-outline-success" onClick={exportMonthlyPdf} disabled={loading}><FontAwesomeIcon icon={faFilePdf} className="me-1"/> Export PDF</button><div className="pnl-month-nav"><button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month"><FontAwesomeIcon icon={faChevronLeft}/></button><b>{monthLabel(month)}</b><button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month"><FontAwesomeIcon icon={faChevronRight}/></button></div></div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}

      <section className="pnl-stats-grid">
        <div className="pnl-stat"><span>MONTHLY NET PNL</span><b className={stats.net >= 0 ? 'positive' : 'negative'}>{money(stats.net)}</b></div>
        <div className="pnl-stat"><span>TOTAL LOSS DAYS</span><b>{stats.lossDays}</b></div>
        <div className="pnl-stat"><span>TOTAL PROFIT DAYS</span><b>{stats.profitDays}</b></div>
        <div className="pnl-stat"><span>AVG LOSS</span><b className="negative">{money(stats.avgLoss)}</b></div>
        <div className="pnl-stat"><span>AVG PROFIT</span><b className="positive">{money(stats.avgProfit)}</b></div>
      </section>

      <section className="pnl-card pnl-calendar-card">
        <div className="pnl-card-head"><div><small>CALENDAR</small><h5>Daily PNL</h5></div><FontAwesomeIcon icon={faChartColumn}/></div>
        <div className="pnl-weekdays">{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <b key={d}>{d}</b>)}</div>
        <div className="pnl-calendar-grid">
          {calendarDays.map((date, i) => {
            if (!date) return <div className="pnl-day empty" key={`empty-${i}`}/>;
            const key = iso(date); const entry = byDate[key]; const value = entry ? Number(entry.pnlAmount) : null;
            return <button key={key} className={`pnl-day ${selectedDate === key ? 'selected' : ''} ${value > 0 ? 'profit' : value < 0 ? 'loss' : ''}`} onClick={() => selectDate(date)}>
              <span>{date.getDate()}</span>{value !== null && <strong>{value >= 0 ? '+' : ''}{Math.round(value).toLocaleString('en-IN')}</strong>}
            </button>;
          })}
        </div>
      </section>

      <section className="pnl-card pnl-detail-card">
        <div className="pnl-card-head"><div><small>SELECTED DATE</small><h5>{selectedDate}</h5></div>{!editing && <button className="btn btn-sm btn-outline-success" onClick={() => setEditing(true)}><FontAwesomeIcon icon={faPenToSquare} className="me-1"/> Edit</button>}</div>
        <div className="pnl-detail-grid">
          <div><span>PNL Amount</span>{editing ? <input value={amount} onChange={e => setAmount(e.target.value)} inputMode="decimal"/> : <b className={Number(amount) >= 0 ? 'positive' : 'negative'}>{amount === '' ? '—' : money(amount)}</b>}</div>
          <div><span>Notes</span>{editing ? <textarea rows="2" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes"/> : <p>{notes || 'No notes'}</p>}</div>
        </div>
        {editing && <div className="pnl-detail-actions"><button className="btn btn-success" disabled={saving || !Number.isFinite(Number(amount))} onClick={save}><FontAwesomeIcon icon={faFloppyDisk} className="me-1"/>{saving ? 'Saving…' : 'Save PNL'}</button><button className="btn btn-light" onClick={() => selectDate(new Date(`${selectedDate}T12:00:00`))}>Cancel</button></div>}
      </section>

      <section className="pnl-card pnl-chart-card">
        <div className="pnl-card-head"><div><small>MONTHLY BAR CHART</small><h5>Daily PNL</h5></div></div>
        {loading ? <div className="pnl-chart-empty">Loading…</div> : !chart.days.length ? <div className="pnl-chart-empty">No PNL data for this month.</div> : <div className="pnl-bar-chart" role="img" aria-label="Daily PNL bar chart">
          {chart.days.map(d => {
            const height = Math.max(6, Math.round(Math.abs(d.value) / chart.max * 100));
            return <button className={`pnl-bar-wrap ${d.value >= 0 ? 'profit' : 'loss'}`} key={d.date} onClick={() => selectDate(new Date(`${d.date}T12:00:00`))} title={`${d.date}: ${money(d.value)}`}>
              <span className="pnl-bar-value">{Math.round(d.value).toLocaleString('en-IN')}</span><i style={{ height: `${height}%` }}/><small>{Number(d.date.slice(8))}</small>
            </button>;
          })}
        </div>}
      </section>
    </main>
  );
}
